import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLang } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import {
  Building2, Landmark, Calculator, Users, BookOpen, Scale, Home as HomeIcon, Award, ArrowRight,
  FileSpreadsheet, Receipt, ClipboardCheck, ShieldCheck, TrendingUp, PieChart, Wallet, Percent,
  Sparkles, CheckCircle2
} from 'lucide-react';

const Services = () => {
  const { t, lang, dir } = useLang();
  const [activeTab, setActiveTab] = useState('accounting');

  const services = [
    { icon: Building2, title: t('services.s1'), desc: t('services.s1d') },
    { icon: Landmark, title: t('services.s2'), desc: t('services.s2d') },
    { icon: Calculator, title: t('services.s3'), desc: t('services.s3d') },
    { icon: Users, title: t('services.s4'), desc: t('services.s4d') },
    { icon: BookOpen, title: t('services.s5'), desc: t('services.s5d') },
    { icon: Scale, title: t('services.s6'), desc: t('services.s6d') },
    { icon: HomeIcon, title: t('services.s7'), desc: t('services.s7d') },
    { icon: Award, title: t('services.s8'), desc: t('services.s8d') }
  ];

  // Accounting & Audit dedicated content
  const aaContent = {
    en: {
      kicker: 'Featured — Accounting & Audit',
      title: 'Accounting & Auditing',
      subtitle: 'Certified UAE accountants and licensed auditors delivering full-cycle finance, tax, and assurance under one roof.',
      tabs: [
        { id: 'accounting', label: 'Accounting' },
        { id: 'audit', label: 'Auditing' }
      ],
      accounting: {
        blurb: 'From day-one bookkeeping to consolidated group reporting — IFRS-compliant, cloud-based, and audit-ready.',
        items: [
          { icon: BookOpen, title: 'Bookkeeping', desc: 'Daily/weekly/monthly bookkeeping on Zoho, QuickBooks, Xero, or Odoo with reconciled bank feeds.' },
          { icon: FileSpreadsheet, title: 'Financial Statements', desc: 'Monthly & annual P&L, Balance Sheet and Cash Flow prepared under IFRS/IFRS for SMEs.' },
          { icon: Receipt, title: 'VAT Compliance', desc: 'FTA VAT registration, quarterly returns, VAT health-check and voluntary disclosures.' },
          { icon: Percent, title: 'Corporate Tax (9%)', desc: 'Corporate Tax registration, small-business relief assessment, transfer-pricing docs and return filing.' },
          { icon: Wallet, title: 'Payroll & WPS', desc: 'Monthly payroll runs, WPS SIF file generation, gratuity computation and payslip issuance.' },
          { icon: TrendingUp, title: 'Management Reports', desc: 'Customised KPI dashboards, budgeting, cash-flow forecasts and variance analysis.' },
          { icon: PieChart, title: 'CFO-on-Demand', desc: 'Fractional CFO support for fundraising, board reporting and strategic decision-making.' },
          { icon: Sparkles, title: 'Accounting Software Setup', desc: 'Selection, migration, chart-of-accounts design and staff training on your ERP or cloud tool.' }
        ]
      },
      audit: {
        blurb: 'Independent audit and assurance services accepted by all UAE freezones and mainland authorities.',
        items: [
          { icon: ClipboardCheck, title: 'Statutory External Audit', desc: 'Annual financial-statement audit compliant with ISA and UAE Commercial Companies Law.' },
          { icon: ShieldCheck, title: 'Freezone-Approved Audit', desc: 'Audit reports accepted by JAFZA, DMCC, IFZA, RAKEZ, DAFZA, SHAMS, Meydan and others.' },
          { icon: FileSpreadsheet, title: 'Internal Audit', desc: 'Risk-based internal audit, SOP reviews and process-improvement recommendations.' },
          { icon: TrendingUp, title: 'Agreed-Upon Procedures', desc: 'AUP engagements for lenders, investors, tenders and Chamber of Commerce submissions.' },
          { icon: Percent, title: 'VAT & Corporate Tax Audit', desc: 'FTA compliance review, pre-audit readiness and representation during FTA queries.' },
          { icon: BookOpen, title: 'Forensic & Fraud Review', desc: 'Special-purpose investigations, fraud examination and litigation-support reports.' },
          { icon: Wallet, title: 'Due Diligence', desc: 'Buy-side and sell-side financial due diligence for M&A and investor onboarding.' },
          { icon: Award, title: 'AML Compliance Review', desc: 'AML/CFT policy review, goAML registration guidance and independent compliance testing.' }
        ]
      },
      whyTitle: 'Why our finance team',
      why: [
        'UAE-licensed auditors registered with the Ministry of Economy.',
        'IFRS, VAT (FTA) and 9% Corporate Tax specialists on staff.',
        'Bilingual reports (Arabic/English) accepted by every freezone.',
        'Fixed-fee monthly retainers — no hidden charges.',
        'Cloud-first workflow with dashboards you access anytime.',
        'Response SLA within 24 business hours.'
      ],
      cta: 'Request an accounting quote',
      cta2: 'Book a free consultation'
    },
    ar: {
      kicker: 'مميز — المحاسبة والتدقيق',
      title: 'المحاسبة والتدقيق',
      subtitle: 'محاسبون معتمدون ومدقّقون مرخّصون في الإمارات، نقدّم دورة كاملة للمحاسبة والضرائب والتدقيق تحت سقف واحد.',
      tabs: [
        { id: 'accounting', label: 'المحاسبة' },
        { id: 'audit', label: 'التدقيق' }
      ],
      accounting: {
        blurb: 'من أول قيد يومي إلى تقارير مجموعة موحّدة — وفق معايير IFRS، على السحابة، وجاهزة للتدقيق.',
        items: [
          { icon: BookOpen, title: 'مسك الدفاتر', desc: 'قيود يومية / أسبوعية / شهرية على Zoho أو QuickBooks أو Xero أو Odoo مع تسوية بنكية.' },
          { icon: FileSpreadsheet, title: 'القوائم المالية', desc: 'إعداد قوائم الدخل والمركز المالي والتدفقات النقدية شهريًا وسنويًا وفق IFRS.' },
          { icon: Receipt, title: 'الامتثال لضريبة القيمة المضافة', desc: 'تسجيل VAT لدى الهيئة، الإقرارات الربعية، فحص الامتثال والإفصاحات الطوعية.' },
          { icon: Percent, title: 'ضريبة الشركات (٩٪)', desc: 'تسجيل ضريبة الشركات، تقييم إعفاء الشركات الصغيرة، توثيق أسعار التحويل والإقرار.' },
          { icon: Wallet, title: 'الرواتب ونظام حماية الأجور', desc: 'تشغيل الرواتب شهريًا، توليد ملف WPS، احتساب مكافأة نهاية الخدمة وقسائم الرواتب.' },
          { icon: TrendingUp, title: 'التقارير الإدارية', desc: 'لوحات مؤشرات مخصّصة، إعداد الميزانيات، توقّعات التدفق النقدي وتحليل الانحرافات.' },
          { icon: PieChart, title: 'مدير مالي عند الطلب', desc: 'دعم CFO جزئي لجمع التمويل، تقارير مجلس الإدارة والقرارات الاستراتيجية.' },
          { icon: Sparkles, title: 'إعداد برامج المحاسبة', desc: 'اختيار البرنامج، الترحيل، تصميم دليل الحسابات وتدريب الفريق على ERP أو نظام سحابي.' }
        ]
      },
      audit: {
        blurb: 'خدمات تدقيق وضمان مستقلة، مقبولة لدى جميع المناطق الحرة والبر الرئيسي في الإمارات.',
        items: [
          { icon: ClipboardCheck, title: 'التدقيق الخارجي القانوني', desc: 'تدقيق القوائم المالية السنوية وفق ISA وقانون الشركات التجارية الإماراتي.' },
          { icon: ShieldCheck, title: 'تدقيق معتمد للمناطق الحرة', desc: 'تقارير تدقيق مقبولة لدى جافزا، DMCC، IFZA، راكز، DAFZA، شمس، ميدان وغيرها.' },
          { icon: FileSpreadsheet, title: 'التدقيق الداخلي', desc: 'تدقيق داخلي قائم على المخاطر، مراجعة الإجراءات وتوصيات تحسين العمليات.' },
          { icon: TrendingUp, title: 'إجراءات متفق عليها', desc: 'ارتباطات AUP للممولين والمستثمرين والمناقصات وغرف التجارة.' },
          { icon: Percent, title: 'تدقيق VAT وضريبة الشركات', desc: 'مراجعة الامتثال لدى الهيئة، الاستعداد للتدقيق وتمثيلكم أمام الاستفسارات.' },
          { icon: BookOpen, title: 'التدقيق الجنائي وكشف الاحتيال', desc: 'تحقيقات لأغراض خاصة، فحص الاحتيال وتقارير دعم التقاضي.' },
          { icon: Wallet, title: 'العناية الواجبة', desc: 'العناية المالية للبيع والشراء لعمليات الاندماج والاستحواذ وضمّ المستثمرين.' },
          { icon: Award, title: 'مراجعة الامتثال لغسل الأموال', desc: 'مراجعة سياسة AML/CFT، إرشاد التسجيل في goAML واختبار امتثال مستقل.' }
        ]
      },
      whyTitle: 'لماذا فريقنا المالي',
      why: [
        'مدقّقون مرخّصون من وزارة الاقتصاد الإماراتية.',
        'خبراء IFRS وضريبة القيمة المضافة وضريبة الشركات ٩٪.',
        'تقارير ثنائية اللغة (عربي/إنجليزي) تُقبل في كل منطقة حرة.',
        'أتعاب شهرية ثابتة — بلا رسوم خفيّة.',
        'مسار عمل سحابي ولوحة مؤشرات لك في أي وقت.',
        'التزام بالردّ خلال ٢٤ ساعة عمل.'
      ],
      cta: 'اطلب عرض سعر محاسبة',
      cta2: 'احجز استشارة مجانية'
    }
  };

  const a = aaContent[lang] || aaContent.en;
  const current = a[activeTab];

  return (
    <div className="pt-28 pb-16" dir={dir}>
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <div className="text-center mb-16">
          <div className="text-xs uppercase tracking-[0.3em] text-[#2563eb] mb-4">— {t('nav.services')} —</div>
          <h1 className="font-display text-5xl md:text-6xl text-[#0a0a0a]">{t('services.title')}</h1>
          <p className="mt-4 text-[#111827]/60 max-w-2xl mx-auto">{t('services.subtitle')}</p>
        </div>

        {/* ==== Featured: Accounting & Audit ==== */}
        <section className="mb-20" data-testid="accounting-audit-section">
          <div className="relative overflow-hidden rounded-3xl border border-[#2563eb]/25 bg-gradient-to-br from-[#f1f5f9]/80 via-[#ffffff]/90 to-[#f8fafc] p-8 md:p-12">
            <div className="absolute top-0 end-0 w-72 h-72 bg-[#2563eb]/8 rounded-full blur-3xl pointer-events-none" />
            <div className="relative">
              <div className="text-xs uppercase tracking-[0.3em] text-[#2563eb] mb-3">— {a.kicker} —</div>
              <h2 className="font-display text-4xl md:text-5xl text-[#0a0a0a] mb-3">{a.title}</h2>
              <p className="text-[#111827]/70 max-w-3xl">{a.subtitle}</p>

              {/* Tabs */}
              <div className="mt-8 inline-flex p-1 rounded-full bg-[#f8fafc] border border-[#2563eb]/20">
                {a.tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    data-testid={`tab-${tab.id}`}
                    className={`px-6 py-2 rounded-full text-sm transition-all ${activeTab === tab.id ? 'bg-gradient-to-r from-[#2563eb] to-[#1d4ed8] text-[#f8fafc] font-semibold shadow-lg shadow-[#2563eb]/20' : 'text-[#111827]/70 hover:text-[#3b82f6]'}`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Blurb */}
              <p className="mt-6 text-sm text-[#111827]/60 max-w-3xl italic">{current.blurb}</p>

              {/* Grid of sub-services */}
              <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {current.items.map((it, i) => {
                  const Ic = it.icon;
                  return (
                    <div key={i} data-testid={`${activeTab}-item-${i}`} className="rounded-xl bg-[#f8fafc]/60 border border-[#2563eb]/15 p-5 hover:border-[#2563eb]/40 hover:-translate-y-0.5 transition-all">
                      <div className="w-10 h-10 rounded-lg bg-[#2563eb]/12 border border-[#2563eb]/25 flex items-center justify-center mb-3">
                        <Ic className="w-4.5 h-4.5 text-[#3b82f6]" strokeWidth={1.75} />
                      </div>
                      <div className="font-display text-lg text-[#0a0a0a] mb-1.5">{it.title}</div>
                      <div className="text-xs text-[#111827]/60 leading-relaxed">{it.desc}</div>
                    </div>
                  );
                })}
              </div>

              {/* Why us */}
              <div className="mt-10 pt-8 border-t border-[#2563eb]/15">
                <div className="text-xs uppercase tracking-[0.3em] text-[#2563eb] mb-4">— {a.whyTitle} —</div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-3">
                  {a.why.map((w, i) => (
                    <div key={i} className="flex items-start gap-2.5 text-sm text-[#111827]/80">
                      <CheckCircle2 className="w-4 h-4 text-[#3b82f6] mt-0.5 flex-shrink-0" />
                      <span>{w}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* CTAs */}
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/Contact"><Button data-testid="aa-quote-btn" className="gold-button rounded-md h-11 px-6 text-sm">{a.cta} <ArrowRight className="w-4 h-4 ms-2 rtl:hidden" /></Button></Link>
                <Link to="/Contact"><Button data-testid="aa-consult-btn" className="ghost-button rounded-md h-11 px-6 text-sm bg-transparent">{a.cta2}</Button></Link>
              </div>
            </div>
          </div>
        </section>

        {/* ==== Existing 8 core services ==== */}
        <div className="text-center mb-8">
          <div className="text-xs uppercase tracking-[0.3em] text-[#2563eb]">— {lang === 'ar' ? 'المزيد من خدماتنا' : 'More of our services'} —</div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {services.map((s, i) => (
            <div key={i} className="glass-card rounded-2xl p-7 group hover:-translate-y-1 transition-transform">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#2563eb]/25 to-[#2563eb]/5 border border-[#2563eb]/25 flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
                <s.icon className="w-5 h-5 text-[#3b82f6]" />
              </div>
              <h3 className="font-display text-xl text-[#0a0a0a] mb-2">{s.title}</h3>
              <p className="text-sm text-[#111827]/60 leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>

        <div className="mt-16 glass-card rounded-3xl p-12 text-center">
          <h2 className="font-display text-3xl md:text-4xl text-[#0a0a0a]">{t('cta.book_consultation')}</h2>
          <p className="mt-3 text-[#111827]/60">{t('contact.subtitle')}</p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <Link to="/Contact"><Button className="gold-button rounded-md h-11 px-6 text-sm">{t('cta.book_consultation')} <ArrowRight className="w-4 h-4 ms-2 rtl:hidden" /></Button></Link>
            <Link to="/SetupWizard"><Button className="ghost-button rounded-md h-11 px-6 text-sm bg-transparent">{t('cta.start_wizard')}</Button></Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Services;
